<div class="company-page page-shell">
  <mat-toolbar class="header">
    <span>{{ "company.title" | translate }}</span>
    <div class="header-buttons">
      <button
        mat-raised-button
        color="primary"
        type="button"
        (click)="save()"
        [disabled]="saving || !hasChanges || loading"
      >
        <mat-spinner *ngIf="saving" diameter="20" class="dc-btn-spinner"></mat-spinner>
        <mat-icon *ngIf="!saving" class="btn-icon">save</mat-icon>
        <span>{{ saving ? ("saving" | translate) : ("save" | translate) }}</span>
      </button>
    </div>
  </mat-toolbar>

  <form [formGroup]="form" (ngSubmit)="save()" class="company-content">
    <div class="form-section">
      <h3 class="section-title">{{ "company.basicSection" | translate }}</h3>

      <mat-form-field appearance="outline">
        <mat-label>{{ "company.name" | translate }}</mat-label>
        <input matInput formControlName="name" type="text" [disabled]="loading" />
      </mat-form-field>

      <div class="field-row">
        <mat-form-field appearance="outline">
          <mat-label>{{ "company.website" | translate }}</mat-label>
          <input
            matInput
            formControlName="website"
            type="text"
            [disabled]="loading"
          />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>{{ "company.phone" | translate }}</mat-label>
          <input
            matInput
            formControlName="phone"
            type="text"
            [disabled]="loading"
            [errorStateMatcher]="errorStateMatcher"
          />
          <mat-error *ngIf="form.hasError('phoneNumberInvalid', ['phone'])">
            {{ "phoneNumberInvalid" | translate }}
          </mat-error>
        </mat-form-field>
      </div>

      <mat-form-field appearance="outline">
        <mat-label>{{ "company.address" | translate }}</mat-label>
        <textarea
          matInput
          formControlName="address"
          rows="4"
          [disabled]="loading"
        ></textarea>
      </mat-form-field>
    </div>

    <div class="form-section">
      <h3 class="section-title">{{ "company.contactPersonSection" | translate }}</h3>
      <p class="section-hint">{{ "company.contactPersonHint" | translate }}</p>

      <mat-form-field appearance="outline" class="employee-picker">
        <mat-label>{{ "company.contactPersonEmail" | translate }}</mat-label>
        <input
          #contactPersonSearchInput
          matInput
          type="text"
          [formControl]="contactPersonSearchCtrl"
          [matAutocomplete]="contactPersonAuto"
          #contactPersonAutoTrigger="matAutocompleteTrigger"
          [disabled]="loading"
          [placeholder]="'digitalCards.searchEmployeePlaceholder' | translate"
          (click)="onContactPersonTriggerClick()"
          (blur)="onContactPersonBlur()"
          autocomplete="off"
          role="combobox"
        />
        <button
          *ngIf="selectedContactPerson && !loading"
          type="button"
          matSuffix
          class="employee-picker__clear"
          [attr.aria-label]="'cancel' | translate"
          (mousedown)="$event.preventDefault()"
          (click)="clearContactPerson()"
        >
          <mat-icon>close</mat-icon>
        </button>
        <mat-icon
          matSuffix
          class="employee-picker__chevron"
          aria-hidden="true"
          (mousedown)="$event.preventDefault()"
          (click)="
            contactPersonSearchInput.focus(); contactPersonSearchInput.click()
          "
          >arrow_drop_down</mat-icon
        >
        <mat-autocomplete
          #contactPersonAuto="matAutocomplete"
          class="employee-picker__panel"
          (optionSelected)="onContactPersonSelected($event)"
        >
          <mat-option value="">{{
            "company.contactPersonNone" | translate
          }}</mat-option>
          <ng-container *ngIf="contactPersonOptions.length === 0; else contactPersonList">
            <mat-option disabled>{{
              "digitalCards.searchEmployeeEmpty" | translate
            }}</mat-option>
          </ng-container>
          <ng-template #contactPersonList>
            <mat-option
              *ngFor="
                let employee of contactPersonOptions;
                trackBy: contactPersonOptionTrackBy
              "
              [value]="employee"
              class="employee-picker__option"
            >
              <span class="employee-picker__option-name">{{
                employeeFullName(employee) || employeeEmail(employee)
              }}</span>
              <span
                *ngIf="employeeFullName(employee) && employeeEmail(employee)"
                class="employee-picker__option-email"
                >{{ employeeEmail(employee) }}</span
              >
            </mat-option>
          </ng-template>
        </mat-autocomplete>
      </mat-form-field>
    </div>

    <div class="form-section">
      <h3 class="section-title">
        {{ "company.printCardOrdersSection" | translate }}
      </h3>
      <p class="section-hint">
        {{ "company.printCardOrderApproverHint" | translate }}
      </p>

      <mat-form-field appearance="outline" class="employee-picker">
        <mat-label>{{
          "company.printCardOrderApprover" | translate
        }}</mat-label>
        <input
          #approverSearchInput
          matInput
          type="text"
          [formControl]="approverSearchCtrl"
          [matAutocomplete]="approverAuto"
          #approverAutoTrigger="matAutocompleteTrigger"
          [disabled]="loading"
          [placeholder]="'digitalCards.searchEmployeePlaceholder' | translate"
          (click)="onApproverTriggerClick()"
          (blur)="onApproverBlur()"
          autocomplete="off"
          role="combobox"
        />
        <button
          *ngIf="selectedApprover && !loading"
          type="button"
          matSuffix
          class="employee-picker__clear"
          [attr.aria-label]="'cancel' | translate"
          (mousedown)="$event.preventDefault()"
          (click)="clearApprover()"
        >
          <mat-icon>close</mat-icon>
        </button>
        <mat-icon
          matSuffix
          class="employee-picker__chevron"
          aria-hidden="true"
          (mousedown)="$event.preventDefault()"
          (click)="approverSearchInput.focus(); approverSearchInput.click()"
          >arrow_drop_down</mat-icon
        >
        <mat-autocomplete
          #approverAuto="matAutocomplete"
          class="employee-picker__panel"
          (optionSelected)="onApproverSelected($event)"
        >
          <mat-option value="">{{
            "company.printCardOrderApproverNone" | translate
          }}</mat-option>
          <mat-option *ngIf="printCardOrderApproversLoading" disabled>
            <mat-spinner diameter="24" class="dbc1-spinner dbc1-spinner--md"></mat-spinner>
          </mat-option>
          <ng-container
            *ngIf="
              !printCardOrderApproversLoading &&
              filteredPrintCardOrderApprovers.length === 0
            "
          >
            <mat-option disabled>{{
              "digitalCards.searchEmployeeEmpty" | translate
            }}</mat-option>
          </ng-container>
          <mat-option
            *ngFor="
              let approver of filteredPrintCardOrderApprovers;
              trackBy: approverOptionTrackBy
            "
            [value]="approver"
            class="employee-picker__option"
          >
            <span class="employee-picker__option-name">{{
              approver.fullName || approver.email
            }}</span>
            <span
              *ngIf="approver.fullName"
              class="employee-picker__option-email"
              >{{ approver.email }}</span
            >
          </mat-option>
        </mat-autocomplete>
      </mat-form-field>
    </div>

    <div class="form-section">
      <h3 class="section-title">{{ "company.logoSection" | translate }}</h3>

      <app-image-uploader
        class="image-uploader"
        [allowedFileExtensions]="rasterMimeTypes"
        [previewUrl]="previewUrl"
        (onFileChange)="fileChange($event)"
        (onFileRemove)="removePhoto()"
        [previewImageStyles]="
          imageService.getImageStyle(form.get('imageWidth').value)
        "
      ></app-image-uploader>

      <mat-form-field appearance="outline" class="width-field">
        <mat-label>{{ "imageWidth" | translate }}</mat-label>
        <input
          matInput
          formControlName="imageWidth"
          type="number"
          min="0"
          [disabled]="loading"
        />
        <span matSuffix>px</span>
      </mat-form-field>
    </div>

    <div class="form-section">
      <h3 class="section-title">{{ "company.fontSection" | translate }}</h3>

      <mat-form-field appearance="outline">
        <mat-label>{{ "company.fontFamilyLabel" | translate }}</mat-label>
        <input
          matInput
          formControlName="brandFontFamily"
          type="text"
          [disabled]="loading"
          [placeholder]="'company.fontFamilyPlaceholder' | translate"
        />
      </mat-form-field>

      <div class="font-upload-row">
        <input
          #fontFileInput
          class="font-upload-input"
          type="file"
          [attr.accept]="brandFontAccept"
          (change)="onBrandFontFileChange(fontFileInput.files); fontFileInput.value = ''"
        />
        <button
          mat-stroked-button
          type="button"
          (click)="fontFileInput.click()"
          [disabled]="loading || uploadingFont"
        >
          <mat-icon>upload_file</mat-icon>
          {{ "company.fontChooseFile" | translate }}
        </button>
        <span class="font-upload-name">
          {{
            brandFontFileName
              ? brandFontFileName
              : brandFontFileUrl
                ? ("company.fontUploadedName" | translate)
                : ("company.fontNoFile" | translate)
          }}
        </span>
        @if (brandFontFileUrl) {
          <button
            mat-button
            type="button"
            color="warn"
            (click)="clearBrandFont()"
            [disabled]="loading || uploadingFont"
          >
            {{ "company.fontRemove" | translate }}
          </button>
        }
        @if (uploadingFont) {
          <mat-spinner diameter="18"></mat-spinner>
        }
      </div>
    </div>
  </form>

  <div class="dbc1-loading dbc1-loading--overlay" *ngIf="loading">
    <app-loading></app-loading>
  </div>
</div>
